import {
  Autocomplete,
  ComboboxItem,
  Loader,
  OptionsFilter,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";

import { useProgressiveDebtorFetch } from "hooks/useProgressiveDebtorFetch";
import { useEffect, useMemo, useState } from "react";

type IDebtorSelectProps = {
  name: string;
  ariaLabel: string;
  label?: string;
  placeholder?: string;
  value: number | null;
  onChange: (debtorId: number | null, displayValue: string) => void;
  disabled?: boolean;
};

const DebtorSelect = ({
  name,
  ariaLabel,
  label,
  placeholder,
  value,
  disabled,
  onChange,
}: IDebtorSelectProps) => {
  const [debtorInputValue, setDebtorInputValue] = useState("");
  const [debouncedInput] = useDebouncedValue(debtorInputValue, 150);
  const {
    debtors,
    loading: debtorLoading,
    error: debtorError,
  } = useProgressiveDebtorFetch();
  const debtorOptions = useMemo<ComboboxItem[]>(() => {
    return debtors.map((debtor) => ({
      value: `${debtor.id}`,
      label: `${debtor.name} [${debtor.id}]`,
    }));
  }, [debtors]);

  const optionsFilter: OptionsFilter = ({ options }) => {
    const searchWords = debouncedInput.toLowerCase().trim().split(" ");

    return (options as ComboboxItem[]).filter((option) => {
      const labelWords = option.label?.toLowerCase().trim().split(" ") ?? [];
      return searchWords.every((word) =>
        labelWords.some((labelWord) => labelWord.includes(word)),
      );
    });
  };
  useEffect(() => {
    const match = debtorOptions.find((d) => Number(d.value) === value);
    if (match) {
      setDebtorInputValue(match.label);
    } else {
      setDebtorInputValue("");
    }
  }, [value, debtorOptions]);

  return (
    <Autocomplete
      name={name}
      aria-label={ariaLabel}
      label={label ?? "Title"}
      placeholder={placeholder ?? "Enter value"}
      data={debtorOptions}
      filter={optionsFilter}
      value={debtorInputValue}
      rightSection={debtorLoading ? <Loader size="xs" /> : null}
      comboboxProps={{
        withinPortal: true,
        zIndex: 3001,
      }}
      onChange={setDebtorInputValue} // updates as user types
      onOptionSubmit={(selected) => {
        const match = debtorOptions.find(
          (d) => d.label === selected || d.value === selected,
        );
        if (!match) {
          onChange(null, selected);
          return;
        }

        const id = match ? Number(match.value) : 0;
        const inputValue = match?.label ?? selected;

        setDebtorInputValue(inputValue); // show full label in input
        onChange(id, inputValue);
      }}
      onClear={() => onChange(null, "")}
      clearable
      w={400}
      disabled={disabled}
      error={debtorError ?? undefined}
      style={{ zIndex: 3001 }}
      styles={{ label: { fontWeight: 600 } }}
    />
  );
};

export default DebtorSelect;
